import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Car, 
  Users, 
  Shield, 
  DollarSign, 
  MapPin, 
  Clock,
  Star,
  ArrowRight,
  CheckCircle
} from 'lucide-react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Grid, 
  Card, 
  CardContent,
  Chip,
  Fade,
  Slide,
  Grow,
  Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled components for enhanced visual effects
const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%)',
  color: 'white',
  overflow: 'hidden',
  minHeight: '80vh',
  display: 'flex',
  alignItems: 'center',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
  }
}));

const StyledButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5, 4),
  fontSize: '1.1rem',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.2)',
  },
  ...(variant === 'outlined' && {
    border: '2px solid rgba(255, 255, 255, 0.8)',
    color: 'white',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.1)',
      border: '2px solid white',
    }
  })
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.15)',
  }
}));

const StatsCard = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(3),
  borderRadius: 16,
  background: 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
  }
}));

const QuickMatchCard = styled(Paper)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.15)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  borderRadius: 20,
  padding: theme.spacing(4),
  color: 'white',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%)',
  boxShadow: '0 8px 32px rgba(99, 102, 241, 0.3)',
  margin: '0 auto',
  marginBottom: theme.spacing(2),
}));

const Home = () => {
  const { user } = useAuth();

  const features = [
    {
      icon: Users,
      title: 'Verified Commuters',
      description: 'Connect with pre-verified users for safe and reliable rides.'
    },
    {
      icon: DollarSign,
      title: 'Split Fares',
      description: 'Share your ride costs and save money on daily commutes.'
    },
    {
      icon: Shield,
      title: 'Safe & Secure',
      description: 'Built-in safety features and real-time ride tracking.'
    },
    {
      icon: MapPin,
      title: 'Smart Matching',
      description: 'AI-powered matching for optimal route and timing.'
    }
  ];

  const stats = [
    { number: '10K+', label: 'Active Users' },
    { number: '50K+', label: 'Rides Shared' },
    { number: '₹2M+', label: 'Money Saved' },
    { number: '4.8★', label: 'User Rating' }
  ];

  const benefits = [
    'Save up to 60% on your daily commute',
    'Meet like-minded commuters',
    'Reduce your carbon footprint',
    'Flexible scheduling options',
    'Real-time ride tracking',
    '24/7 customer support'
  ];

  return (
    <Box sx={{ minHeight: '100vh' }}>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg" sx={{ py: 8, position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} lg={6}>
              <Fade in timeout={800}>
                <Box>
                  <Typography 
                    variant="h2" 
                    sx={{ 
                      fontWeight: 'bold', 
                      mb: 3,
                      fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                      lineHeight: 1.2
                    }}
                  >
                    Share Rides,{' '}
                    <Box component="span" sx={{ color: '#fbbf24' }}>
                      Save Money
                    </Box>
                  </Typography>
                  
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.9)', 
                      mb: 4,
                      lineHeight: 1.6,
                      fontWeight: 400
                    }}
                  >
                    Connect with verified commuters and split your ride fares. 
                    A social-first cab-sharing assistant for daily commuters.
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    {user ? (
                      <StyledButton
                        component={Link}
                        to="/dashboard"
                        variant="contained"
                        size="large"
                        endIcon={<ArrowRight size={20} />}
                        sx={{ background: 'white', color: '#6366f1' }}
                      >
                        Go to Dashboard
                      </StyledButton>
                    ) : (
                      <>
                        <StyledButton
                          component={Link}
                          to="/register"
                          variant="contained"
                          size="large"
                          endIcon={<ArrowRight size={20} />}
                          sx={{ background: 'white', color: '#6366f1' }}
                        >
                          Get Started
                        </StyledButton>
                        <StyledButton
                          component={Link}
                          to="/login"
                          variant="outlined"
                          size="large"
                        >
                          Sign In
                        </StyledButton>
                      </>
                    )}
                  </Box>
                </Box>
              </Fade>
            </Grid>
            
            <Grid item xs={12} lg={6}>
              <Slide direction="left" in timeout={1000}>
                <QuickMatchCard>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Box sx={{ 
                      width: 48, 
                      height: 48, 
                      background: 'rgba(255, 255, 255, 0.2)', 
                      borderRadius: '50%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <Car size={24} />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Quick Ride Match
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Find your perfect ride partner
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <MapPin size={20} style={{ color: '#fbbf24' }} />
                      <Typography variant="body2">Mumbai Central → Andheri</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Clock size={20} style={{ color: '#fbbf24' }} />
                      <Typography variant="body2">Today, 9:00 AM</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <DollarSign size={20} style={{ color: '#fbbf24' }} />
                      <Typography variant="body2">₹150 (split fare)</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Users size={20} style={{ color: '#fbbf24' }} />
                      <Typography variant="body2">2 seats available</Typography>
                    </Box>
                  </Box>
                </QuickMatchCard>
              </Slide>
            </Grid>
          </Grid>
        </Container>
      </HeroSection>

      {/* Stats Section */}
      <Box sx={{ py: 8, background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Slide direction="up" in timeout={600 + index * 200}>
                  <StatsCard>
                    <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#6366f1', mb: 1 }}>
                      {stat.number}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#64748b', fontWeight: 500 }}>
                      {stat.label}
                    </Typography>
                  </StatsCard>
                </Slide>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: 10, background: 'white' }}>
        <Container maxWidth="lg">
          <Fade in timeout={800}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 2, color: '#1e293b' }}>
                Why Choose LinkCab?
              </Typography>
              <Typography variant="h6" sx={{ color: '#64748b', maxWidth: 600, mx: 'auto' }}>
                Experience the future of ride-sharing with our innovative platform designed for commuters.
              </Typography>
            </Box>
          </Fade>
          
          <Grid container spacing={4}>
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Grid item xs={12} sm={6} lg={3} key={index}>
                  <Grow in timeout={800 + index * 200}>
                    <FeatureCard>
                      <CardContent sx={{ textAlign: 'center', p: 4 }}>
                        <IconContainer>
                          <Icon size={32} color="white" />
                        </IconContainer>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1e293b' }}>
                          {feature.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.6 }}>
                          {feature.description}
                        </Typography>
                      </CardContent>
                    </FeatureCard>
                  </Grow>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ 
        py: 10, 
        background: 'linear-gradient(135deg, #eff6ff 0%, #f3e8ff 100%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(99, 102, 241, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(168, 85, 247, 0.05) 0%, transparent 50%)',
          pointerEvents: 'none',
        }
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} lg={6}>
              <Fade in timeout={800}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 3, color: '#1e293b' }}>
                    Start Saving Today
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#64748b', mb: 4, lineHeight: 1.6 }}>
                    Join thousands of commuters who are already saving money and making new connections through LinkCab.
                  </Typography>
                  
                  <Grid container spacing={2}>
                    {benefits.map((benefit, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <CheckCircle size={20} style={{ color: '#10b981' }} />
                          <Typography variant="body2" sx={{ color: '#374151' }}>
                            {benefit}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                  
                  <Box sx={{ mt: 4 }}>
                    <StyledButton
                      component={Link}
                      to={user ? "/dashboard" : "/register"}
                      variant="contained"
                      size="large"
                      endIcon={<ArrowRight size={20} />}
                      sx={{ 
                        background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%)',
                        color: 'white'
                      }}
                    >
                      {user ? 'Go to Dashboard' : 'Get Started Now'}
                    </StyledButton>
                  </Box>
                </Box>
              </Fade>
            </Grid>
            
            <Grid item xs={12} lg={6}>
              <Slide direction="right" in timeout={1000}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ 
                    width: 200, 
                    height: 200, 
                    mx: 'auto', 
                    background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 20px 60px rgba(99, 102, 241, 0.3)',
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%, 100%': {
                        transform: 'scale(1)',
                      },
                      '50%': {
                        transform: 'scale(1.05)',
                      },
                    },
                  }}>
                    <Car size={80} color="white" />
                  </Box>
                </Box>
              </Slide>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Home; 